import {
  ComponentProps,
  ComponentType,
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import { parseUrlNum, useUrlParams } from "~@/hooks/useUrlParams";

export type HomeContext = {
  cityId: number | undefined;
  setCityId: Dispatch<SetStateAction<number | undefined>>;
  // J-Academy has its own cities directory, kept apart from cityId
  academyCityId: number | undefined;
  setAcademyCityId: Dispatch<SetStateAction<number | undefined>>;
  academyVenueId: number | undefined;
  setAcademyVenueId: Dispatch<SetStateAction<number | undefined>>;
  tournamentId: number | undefined;
  setTournamentId: Dispatch<SetStateAction<number | undefined>>;
  detailsCategoryId: number | undefined;
  setDetailsCategoryId: Dispatch<SetStateAction<number | undefined>>;
  resultsCategoryId: number | undefined;
  setResultsCategoryId: Dispatch<SetStateAction<number | undefined>>;
};

// eslint-disable-next-line @typescript-eslint/no-redeclare -- companion object
export const HomeContext = createContext<HomeContext | undefined>(undefined);

export const useHomeContext = () => {
  const context = useContext(HomeContext);

  if (!context) {
    throw new Error("Missing HomeProvider.");
  }

  return context;
};

export const HomeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [cityId, setCityId] = useState<number | undefined>();
  const [academyCityId, setAcademyCityId] = useState<number | undefined>();
  const [academyVenueId, setAcademyVenueId] = useState<number | undefined>();
  const [tournamentId, setTournamentId] = useState<number | undefined>();

  const { searchParams, makeNumSetter } = useUrlParams();

  const detailsCategoryId = parseUrlNum(searchParams, "hm_det_cat");
  const resultsCategoryId = parseUrlNum(searchParams, "hm_res_cat");

  const setDetailsCategoryId = useMemo(() => makeNumSetter("hm_det_cat"), [makeNumSetter]);
  const setResultsCategoryId = useMemo(() => makeNumSetter("hm_res_cat"), [makeNumSetter]);

  return (
    <HomeContext.Provider
      value={{
        cityId,
        setCityId,
        academyCityId,
        setAcademyCityId,
        academyVenueId,
        setAcademyVenueId,
        tournamentId,
        setTournamentId,
        detailsCategoryId,
        setDetailsCategoryId,
        resultsCategoryId,
        setResultsCategoryId,
      }}
    >
      {children}
    </HomeContext.Provider>
  );
};

export const withHomeProvider = <P extends object>(
  Component: ComponentType<P>
): FC<P> => {
  return function WithHomeProvider(props: ComponentProps<typeof Component>) {
    return (
      <HomeProvider>
        <Component {...props} />
      </HomeProvider>
    );
  };
};
